import { Button, Chip, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { MdFileDownload } from 'react-icons/md'
import * as XLSX from 'xlsx'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
// import { COURIER } from '../../constants'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { storesApi, EquipoDiaRow, DeviceTimelineRow } from '../../api'

const columns = [
  { name: 'Id Tradein', uid: 'id_tradein' },
  { name: 'Folio', uid: 'folio' },
  { name: 'Sellos', uid: 'sellos' },
  { name: 'Equipo', uid: 'equipo' },
  { name: 'Estado', uid: 'estado' },
  { name: 'Asignado a recolección', uid: 'asignado' },
  { name: 'Tiempo desde Entregado', uid: 'tiempo_entregado' },
  { name: 'Fecha cotización', uid: 'fecha_cotizacion' },
  { name: 'Ejecutivo', uid: 'ejecutivo' },
  { name: 'Etiqueta', uid: 'etiqueta' },
  { name: 'Tracking', uid: 'tracking' },
  { name: 'Alerta', uid: 'alerta' },
  { name: 'Valor', uid: 'valor' },
  { name: '', uid: 'ficha' }
]

// const MiniKpi: FC<{ value: string, label: string, tone: keyof typeof TONES }> = ({ value, label, tone }) => (
//   <div className='rounded-[20px] py-3 px-4' style={{ background: TONES[tone].bg, color: TONES[tone].ink }}>
//     <div className='text-2xl font-semibold'>{value}</div>
//     <div className='text-xs'>{label}</div>
//   </div>
// )

const fmtDateTime = (iso: string | null): string => {
  if (iso === null) return '—'
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} · ${hh}:${min}`
}

const fmtMoney = (n: number | null): string => {
  if (n === null) return '—'
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })
}

// Tiempo transcurrido desde una fecha (ej. paso a Entregado) hasta ahora.
const fmtElapsed = (iso: string | null): string => {
  if (iso === null) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  let mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 0) mins = 0
  const d = Math.floor(mins / 1440)
  const h = Math.floor((mins % 1440) / 60)
  const m = mins % 60
  if (d > 0) return `${d} d ${h} h`
  if (h > 0) return `${h} h ${m} min`
  return `${m} min`
}

// Etiqueta de estatus del equipo en el detalle. Si está asignado a una recolección
// se muestra la fase de la recolección; si no, el estado crudo del equipo.
const estatusLabel = (e: EquipoDiaRow): string => {
  // Discrepancia = ti_folio_discrepancies sin resolver (con_discrepancia), igual que
  // Arribos. box_state NO determina discrepancia.
  if (e.con_discrepancia) return 'Discrepancia'
  // El estado de caja registrado (box_state) manda para el resto · así la caja 2 no
  // asignada no se muestra en tránsito solo porque la caja 1 se retiró (RN-17).
  switch (e.box_state) {
    case 'RECIBIDO_BODEGA': return 'Recibido en bodega'
    case 'EN_TRANSITO': return 'En tránsito'
    case 'EN_ESPERA': return 'En espera de recolección'
  }
  // Fallback derivado (equipos sin eventos de caja registrados aún).
  if (e.asignado) return e.estado === 'en_camino' ? 'En tránsito' : 'En espera de recolección'
  return e.estado === 'custodia' ? 'Custodia' : e.estado === 'en_camino' ? 'En camino' : 'Entregado'
}

// Orden por defecto del detalle: 1 En tránsito · 2 En espera de recolección ·
// 3 Custodia · 4 Entregado.
const estatusRank = (e: EquipoDiaRow): number => {
  switch (estatusLabel(e)) {
    case 'En tránsito':
    case 'En camino': return 1
    case 'En espera de recolección': return 2
    case 'Custodia': return 3
    case 'Entregado': return 4
    default: return 5
  }
}

// Color propio por ETIQUETA de estatus (badge suave: fondo claro + texto del mismo tono).
// Entregado azul · Custodia café · En espera de recolección rosa · En tránsito verde.
const ESTATUS_COLORS: Record<string, { bg: string, ink: string }> = {
  Entregado: { bg: '#E7EEFD', ink: '#2952CC' }, // azul
  Custodia: { bg: '#EFE6DC', ink: '#7A4E24' }, // café
  'En espera de recolección': { bg: '#FCE7EF', ink: '#B4306B' }, // rosa
  'En tránsito': { bg: '#E7F3DE', ink: '#3B6D11' }, // verde
  'En camino': { bg: '#E7F3DE', ink: '#3B6D11' }, // verde (en tránsito sin asignar · borde)
  Discrepancia: { bg: '#FAECE7', ink: '#993C1D' }, // rojo (faltante)
  'Recibido en bodega': { bg: '#EAF3DE', ink: '#3B6D11' } // verde oscuro
}

type SubTab = 'entregado' | 'custodia' | 'espera' | 'transito' | 'no_recibido'
const SUBTABS: Array<{ key: SubTab, label: string }> = [
  { key: 'entregado', label: 'Entregado' },
  { key: 'custodia', label: 'Custodia' },
  { key: 'espera', label: 'En espera de recolección' },
  { key: 'transito', label: 'En tránsito' },
  { key: 'no_recibido', label: 'Discrepancias' }
]

const DetalleTiendaView: FC = () => {
  const { selectedStore, selectedStoreId } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const { enqueueSnackbar } = useSnackbar()
  const [subtab, setSubtab] = useState<SubTab>('entregado')
  const [fichaId, setFichaId] = useState<number | null>(null)
  const [q, setQ] = useState('')

  const fetchDetail = useCallback(async () => {
    if (selectedStoreId === null) throw new Error('Selecciona una tienda desde el dashboard')
    return await storesApi.detail(selectedStoreId, idSeller)
  }, [selectedStoreId, idSeller])
  const fetchEquipos = useCallback(async () => {
    if (selectedStoreId === null) return [] as EquipoDiaRow[]
    return await storesApi.equiposToday(selectedStoreId, idSeller)
  }, [selectedStoreId, idSeller])

  const detail = useApi(fetchDetail, [selectedStoreId, idSeller])
  const equipos = useApi(fetchEquipos, [selectedStoreId, idSeller])

  const tienda = detail.data?.store ?? selectedStore ?? 'Tienda'

  // Buckets por estado del ciclo de caja (RN-26): próximo retiro (sin asignar),
  // en espera de recolección, en tránsito y no recibido.
  const buckets = useMemo(() => {
    const rows = equipos.data ?? []
    const b: Record<SubTab, EquipoDiaRow[]> = { entregado: [], custodia: [], espera: [], transito: [], no_recibido: [] }
    for (const e of rows) {
      const bs = e.box_state
      // "Discrepancias" es EXCLUSIVAMENTE ti_folio_discrepancies sin resolver
      // (con_discrepancia). Bucket exclusivo.
      if (e.con_discrepancia) { b.no_recibido.push(e); continue }
      // En tránsito (ya retirado) · bucket exclusivo (RN-17).
      if (bs === 'EN_TRANSITO' || (bs == null && e.estado === 'en_camino')) { b.transito.push(e); continue }
      // Recibido en bodega → no se muestra en pendientes.
      if (bs === 'RECIBIDO_BODEGA') continue
      // En tienda: clasifica por ESTADO real (Entregado/Custodia) SIEMPRE, aunque esté
      // asignado; y si está asignado a una recolección aparece TAMBIÉN en "En espera".
      if (e.estado === 'custodia') b.custodia.push(e)
      else b.entregado.push(e)
      if (e.asignado || bs === 'EN_ESPERA') b.espera.push(e)
    }
    const rank = (a: EquipoDiaRow, z: EquipoDiaRow): number => estatusRank(a) - estatusRank(z)
    for (const k of Object.keys(b) as SubTab[]) b[k].sort(rank)
    return b
  }, [equipos.data])

  // Buscador (RN-31): folio, tracking, etiqueta, sellos o id dentro del subtab.
  const equiposFiltrados = useMemo(() => {
    const rows = buckets[subtab]
    const term = q.trim().toLowerCase()
    if (term === '') return rows
    return rows.filter((e) =>
      String(e.id_tradein).includes(term) ||
      (e.folio ?? '').toLowerCase().includes(term) ||
      (e.tracking ?? '').toLowerCase().includes(term) ||
      (e.etiqueta ?? '').toLowerCase().includes(term) ||
      (e.seal ?? '').toLowerCase().includes(term) ||
      (e.seal2 ?? '').toLowerCase().includes(term)
    )
  }, [buckets, subtab, q])

  // Exporta los equipos del día (respetando el filtro) a Excel, nombrado con
  // la tienda + fecha y hora de la extracción.
  const handleExport = (): void => {
    const rows = equiposFiltrados
    if (rows.length === 0) {
      enqueueSnackbar('No hay equipos para exportar', { variant: 'info' })
      return
    }

    const data = rows.map(e => ({
      'Id Tradein': e.id_tradein,
      Folio: e.folio ?? '',
      Equipo: e.equipo,
      Estado: estatusLabel(e),
      'Asignado a recolección': e.asignado ? 'Asignado' : 'Sin asignar',
      'Tiempo desde Entregado': fmtElapsed(e.fecha_entregado),
      'Fecha cotización': fmtDateTime(e.fecha_cotizacion),
      Ejecutivo: e.ejecutivo ?? '',
      Etiqueta: e.etiqueta ?? '',
      Tracking: e.tracking ?? '',
      Alerta: e.alerta ?? '',
      Valor: e.valor ?? ''
    }))

    const now = new Date()
    const pad = (n: number): string => String(n).padStart(2, '0')
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
    const safeTienda = tienda.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Equipos')
    XLSX.writeFile(wb, `Detalle_${safeTienda}_${stamp}.xlsx`)
    enqueueSnackbar(`Excel exportado · ${rows.length} equipos`, { variant: 'success' })
  }

  const estadoChip = (e: EquipoDiaRow): JSX.Element => {
    const label = estatusLabel(e)
    const c = ESTATUS_COLORS[label] ?? { bg: '#EDEDED', ink: '#333333' }
    return (
      <span
        className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap'
        style={{ backgroundColor: c.bg, color: c.ink }}
      >
        {label}
      </span>
    )
  }

  const renderCell = (e: EquipoDiaRow, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'id_tradein':
        return <span className='font-medium text-xs'>{e.id_tradein}</span>
      case 'folio':
        return <span className='text-xs'>{e.folio ?? '—'}</span>
      case 'sellos': {
        const sellos = [e.seal, e.seal2].filter(x => x !== null && x !== '').join(' · ')
        return <span className='text-xs'>{sellos !== '' ? sellos : '—'}</span>
      }
      case 'equipo':
        return <span className='text-xs'>{e.equipo}</span>
      case 'estado':
        return estadoChip(e)
      case 'asignado':
        return e.asignado
          ? <Chip size='sm' variant='flat' color='success'>Asignado</Chip>
          : <Chip size='sm' variant='flat' color='default'>Sin asignar</Chip>
      case 'tiempo_entregado':
        return <span className='text-xs'>{fmtElapsed(e.fecha_entregado)}</span>
      case 'fecha_cotizacion':
        return <span className='text-xs'>{fmtDateTime(e.fecha_cotizacion)}</span>
      case 'ejecutivo':
        return <span className='text-xs'>{e.ejecutivo ?? '—'}</span>
      case 'etiqueta':
        return <span className='text-xs'>{e.etiqueta ?? '—'}</span>
      case 'tracking':
        return <span className='text-xs'>{e.tracking ?? '—'}</span>
      case 'alerta':
        return e.alerta !== null
          ? <span className='text-[11px] font-medium text-[#993C1D] bg-[#FAECE7] rounded-full px-2 py-0.5'>{e.alerta}</span>
          : <span className='text-xs text-gray-400'>—</span>
      case 'valor':
        return <span className='text-xs'>{fmtMoney(e.valor)}</span>
      case 'ficha':
        return (
          <button
            className='text-[#4b75f7] font-medium hover:underline text-xs'
            onClick={() => setFichaId(e.id_tradein)}
          >
            Ver ficha
          </button>
        )
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='dashboard' />

      {/* Contexto de la tienda */}
      {/* <Panel>
        <ApiStateView loading={detail.loading} error={detail.error} onRetry={detail.reload}>
          <div className='flex items-start justify-between gap-6 flex-wrap'>
            <div>
              <h1 className='text-xl font-bold text-[#151930]'>{tienda}</h1>
              <p className='text-xs text-[#151930]/60 mt-1'>
                Última recolección {COURIER}:{' '}
                <b>{fmtDateTime(detail.data?.last_picked_up_at ?? null)}</b>
                {detail.data?.dias_desde_ultimo_retiro !== null && detail.data?.dias_desde_ultimo_retiro !== undefined && (
                  <> · hace {detail.data.dias_desde_ultimo_retiro} días</>
                )}
                {detail.data !== null && (detail.data?.dias_desde_ultimo_retiro ?? 0) > 14 && (
                  <span className='ml-2'><Pill tone='rosa'>Excede 14 días</Pill></span>
                )}
              </p>
            </div>
          </div>
          <div className='mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3'>
            <MiniKpi value={String(detail.data?.kpis.entregados ?? 0)} label='Entregados (estado actual)' tone='blue' />
            <MiniKpi value={String(detail.data?.kpis.en_custodia ?? 0)} label='En custodia (esperando)' tone='amber' />
            <MiniKpi value={String(detail.data?.kpis.en_camino ?? 0)} label='En camino' tone='teal' />
            <MiniKpi value={`${detail.data?.kpis.porcentaje_encajado ?? 0}%`} label='% encajado' tone='green' />
            <MiniKpi value={String(detail.data?.kpis.sin_custodia ?? 0)} label='Sin custodia' tone='rosa' />
          </div>
        </ApiStateView>
      </Panel> */}

      {/* Tabla equipos del día */}
      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <h2 className='font-semibold text-[#151930] flex items-center gap-2'>
            {tienda} - Visualización de detalle
            {detail.data?.kpis.listo_para_retiro === true && (
              <span className='inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#EAF3DE] text-[#3B6D11]'>
                ● Listo para retiro
              </span>
            )}
          </h2>
          <div className='flex gap-2'>
            <input
              className='border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white w-56'
              placeholder='Buscar folio, etiqueta, sellos, id…'
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button
              variant='bordered'
              className='border-gray-200 text-xs'
              startContent={<MdFileDownload className='text-base' />}
              isDisabled={equipos.loading || equiposFiltrados.length === 0}
              onPress={handleExport}
            >
              Exportar
            </Button>
          </div>
        </div>

        {/* Subtabs por estado del ciclo de caja (RN-26) */}
        <div className='flex gap-1 mb-4 border-b border-gray-100 flex-wrap'>
          {SUBTABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setSubtab(t.key)}
              className={`text-xs font-medium px-3 py-2 -mb-px border-b-2 transition-colors ${
                subtab === t.key
                  ? 'border-[#37417f] text-[#37417f]'
                  : 'border-transparent text-[#151930]/50 hover:text-[#151930]/80'
              }`}
            >
              {t.label} ({buckets[t.key].length})
            </button>
          ))}
        </div>

        <ApiStateView
          loading={equipos.loading}
          error={equipos.error}
          onRetry={equipos.reload}
          empty={!equipos.loading && equiposFiltrados.length === 0}
          emptyText='No hay equipos en esta sección'
        >
          <LogiTable<EquipoDiaRow>
            columns={columns}
            items={equiposFiltrados}
            renderCell={renderCell}
            rowKey={(e) => e.id_tradein}
            ariaLabel={`${tienda} - Detalle de equipos`}
            loading={equipos.loading}
            emptyText='No hay equipos en esta sección'
            rowsPerPage={10}
            bottomExtra={
              <div className='flex items-center justify-between text-xs text-[#151930]/60'>
                <span>Mostrando {equiposFiltrados.length} de {equipos.data?.length ?? 0} equipos</span>
              </div>
            }
          />
        </ApiStateView>
      </Panel>

      <FichaModal idTradein={fichaId} onClose={() => setFichaId(null)} />
    </div>
  )
}

// Ficha / línea de tiempo del ciclo de caja de un dispositivo (RN-29).
const FichaModal: FC<{ idTradein: number | null, onClose: () => void }> = ({ idTradein, onClose }) => {
  const [rows, setRows] = useState<DeviceTimelineRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (idTradein === null) { setRows(null); return }
    let vivo = true
    setLoading(true)
    storesApi.deviceTimeline(idTradein)
      .then((r) => { if (vivo) setRows(r) })
      .catch(() => { if (vivo) setRows([]) })
      .finally(() => { if (vivo) setLoading(false) })
    return () => { vivo = false }
  }, [idTradein])

  const EVENTO_LABEL: Record<string, string> = {
    EN_ESPERA: 'En espera de recolección',
    EN_TRANSITO: 'En tránsito',
    RECIBIDO_BODEGA: 'Recibido en bodega',
    NO_RECIBIDO: 'No recibido',
    DESASOCIADO: 'Desasociado',
    REVERTIDO: 'Revertido (cancelación)',
    REAGENDADO: 'Reagendado'
  }

  return (
    <Modal isOpen={idTradein !== null} onClose={onClose} size='lg' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-0.5'>
          <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Ficha del dispositivo</span>
          <span className='text-base font-bold text-[#151930]'>Trade-In #{idTradein}</span>
        </ModalHeader>
        <ModalBody className='pb-6'>
          {loading && <p className='text-sm text-[#151930]/60'>Cargando línea de tiempo…</p>}
          {!loading && rows !== null && rows.length === 0 && (
            <p className='text-sm text-[#151930]/60'>Sin eventos de ciclo de caja registrados.</p>
          )}
          {!loading && rows !== null && rows.length > 0 && (
            <ol className='relative border-l border-[#e2e5f0] ml-2'>
              {rows.map((ev) => (
                <li key={ev.id} className='ml-4 pb-4 last:pb-0'>
                  <span className='absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-[#37417f]' />
                  <div className='text-sm font-medium text-[#151930]'>{EVENTO_LABEL[ev.evento] ?? ev.evento}</div>
                  <div className='text-xs text-[#151930]/55'>
                    {fmtDateTime(ev.created_at)} · {ev.actor_tipo} · {ev.fuente}
                    {ev.id_collection !== null ? ` · agend. #${ev.id_collection}` : ''}
                  </div>
                  {ev.motivo !== null && ev.motivo !== '' && (
                    <div className='text-xs text-[#993C1D] mt-0.5'>Motivo: {ev.motivo}</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default DetalleTiendaView
